import { FC, useCallback, ReactNode, useMemo, useEffect } from 'react';
import { defineMessages, useIntl, FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import classNames from 'classnames';
import Joi from 'joi';
import countries from 'i18n-iso-countries';
import enLocale from 'i18n-iso-countries/langs/en.json';
import Button, { ButtonType } from '../../../../components/Button/Button';
import MessageBlock, {
	BlockStyle,
	MessageType,
} from '../../../../components/MessageBlock/MessageBlock';
import PreorderActions from '../../../../redux/PreorderSlice/PreorderActions';
import { getTravelRuleForm } from '../../../../redux/PreorderSlice/PreorderSelectors';
import { useAppDispatch } from '../../../../redux/Store';
import useValidation from '../../../../hooks/useValidation';
import useDebounce from '../../../../hooks/useDebounce';
import Input from '../../../../components/Input/Input';
import SelectInput from '../../../../components/SelectInput/SelectInput';
import Checkbox from '../../../../components/Checkbox/Checkbox';
import DatePicker from '../../../../components/DatePicker/DatePicker';

import styles from './PreorderTravelRule.module.scss';
import TestIds from '../../../../tests/TestIds';
import {
	PreorderStage,
	TravelRuleType,
	TravelRuleForm,
} from '../../../../redux/PreorderSlice/PreorderSlice';

countries.registerLocale(enLocale);

export const messages = defineMessages({
	enterYourInformation: {
		id: 'payments.enter_your_information',
		defaultMessage: 'Enter your information',
	},
	personal: {
		id: 'payments.personal',
		defaultMessage: 'Personal',
	},
	business: {
		id: 'payments.business',
		defaultMessage: 'Business',
	},
	firstName: {
		id: 'payments.first_name',
		defaultMessage: 'First name',
	},
	firstAndMiddleNames: {
		id: 'payments.first_and_middle_names',
		defaultMessage: 'First and middle name(s)',
	},
	lastName: {
		id: 'payments.last_name',
		defaultMessage: 'Last name',
	},
	lastNames: {
		id: 'payments.last_names',
		defaultMessage: 'Last name(s)',
	},
	dateOfBirth: {
		id: 'payments.date_of_birth',
		defaultMessage: 'Date of birth',
	},
	dateOfBirthPlaceholder: {
		id: 'payments.date_of_birth_placeholder',
		defaultMessage: 'YYYY-MM-DD',
	},
	companyName: {
		id: 'payments.company_name',
		defaultMessage: 'Company name',
	},
	companyNamePlaceholder: {
		id: 'payments.company_name_placeholder',
		defaultMessage: 'e.g. Internet Company Limited',
	},
	companyCode: {
		id: 'payments.company_code',
		defaultMessage: 'Company code',
	},
	companyCodePlaceholder: {
		id: 'payments.company_code_placeholder',
		defaultMessage: 'e.g. 12345678',
	},
	countryOfResidence: {
		id: 'payments.country_of_residence',
		defaultMessage: 'Country of residence',
	},
	countryOfRegistration: {
		id: 'payments.country_of_registration',
		defaultMessage: 'Country of registration',
	},
	email: {
		id: 'payments.email',
		defaultMessage: 'Email',
	},
	enterEmailAddress: {
		id: 'payments.enter_email_address',
		defaultMessage: 'Enter email address',
	},
	emailDisclaimer: {
		id: 'payments.email_disclaimer',
		defaultMessage: 'Email will be used for sending status updates and receipts.',
	},
	saveTravelRuleInformation: {
		id: 'payments.save_travel_rule_information',
		defaultMessage: 'Save this information to speed up future form completions.',
	},
	travelRuleConfirm: {
		id: 'payments.travel_rule_confirm',
		defaultMessage:
			'I confirm that the information I have provided is accurate and complete. I have reviewed and understood the <privacyLink>Privacy Policy</privacyLink> and <shopperDisclaimerLink>Shopper Disclaimer</shopperDisclaimerLink>',
	},
	button: {
		id: 'payments.next',
		defaultMessage: 'Next',
	},
	metaDescription: {
		id: 'payments.preorder_meta',
		defaultMessage: 'Pre-Order information',
	},
	travelRuleInfo: {
		id: 'payments.travel_rule_info',
		defaultMessage:
			'We must collect your billing details to meet regulatory requirements. Your personal data is securely stored.',
	},
	x_lessOrEqual_y: {
		id: 'inputErrors.x_less_or_equal_y',
		defaultMessage: '"{Field}" length must be less than or equal to {Max} characters long',
	},
});

const useFormValidator = () => {
	const { formatMessage } = useIntl();
	const { travelRuleType } = useSelector(getTravelRuleForm);

	return useMemo(
		() =>
			Joi.object<Partial<TravelRuleForm>>({
				firstName: Joi.string()
					.allow('', null)
					.max(255)
					.message(
						formatMessage(messages.x_lessOrEqual_y, {
							Field: formatMessage(messages.firstName),
							Max: 255,
						})
					),
				lastName: Joi.string()
					.allow('', null)
					.max(255)
					.message(
						formatMessage(messages.x_lessOrEqual_y, {
							Field: formatMessage(messages.lastName),
							Max: 255,
						})
					),
				companyName: Joi.string()
					.allow('', null)
					.max(255)
					.message(
						formatMessage(messages.x_lessOrEqual_y, {
							Field: formatMessage(messages.companyName),
							Max: 255,
						})
					),
				companyCode: Joi.string()
					.allow('', null)
					.max(255)
					.message(
						formatMessage(messages.x_lessOrEqual_y, {
							Field: formatMessage(messages.companyCode),
							Max: 255,
						})
					),
				email: Joi.string()
					.allow('', null)
					.email({ tlds: { allow: false } }),
			}),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[travelRuleType]
	);
};

const Form: FC<{ validationResult: Record<keyof TravelRuleForm, string> }> = ({
	validationResult,
}) => {
	const dispatch = useAppDispatch();
	const { formatMessage } = useIntl();
	const {
		travelRuleType,
		firstName,
		lastName,
		dateOfBirth,
		companyName,
		companyCode,
		countryOfResidence,
		countryOfRegistration,
		email,
	} = useSelector(getTravelRuleForm);
	const debouncedEmailError = useDebounce(validationResult.email, 1000);

	const countryOptions = useMemo(() => {
		const countriesList = Object.keys(countries.getAlpha3Codes());
		const options = countriesList.reduce((acc: any, curr: any) => {
			const item = {
				value: curr,
				label: countries?.getName(curr, 'en')?.replace(', Province of China', ''),
			};
			acc.push(item);
			return acc;
		}, []);
		return options;
	}, []);

	useEffect(() => {
		const defaultCountry = countryOptions.find((c: any) => c.value === 'EST')?.value;
		if (!countryOfResidence && defaultCountry) {
			dispatch(PreorderActions.setCountryOfResidence(defaultCountry));
		}
		if (!countryOfRegistration && defaultCountry) {
			dispatch(PreorderActions.setCountryOfRegistration(defaultCountry));
		}
	}, [countryOptions, countryOfResidence, countryOfRegistration, dispatch]);

	return (
		<div>
			<div className={styles.typeSelect} data-cy={TestIds.PreorderTravelRuleTypeSelect}>
				<span
					data-cy={TestIds.PreorderTravelRuleTypePersonal}
					className={classNames(styles.typeSelectLabel, {
						[styles.isSelected]: travelRuleType === TravelRuleType.Personal,
					})}
					onClick={() =>
						dispatch(PreorderActions.setTravelRuleType(TravelRuleType.Personal))
					}
				>
					{formatMessage(messages.personal)}
				</span>
				<span
					data-cy={TestIds.PreorderTravelRuleTypeBusiness}
					className={classNames(styles.typeSelectLabel, {
						[styles.isSelected]: travelRuleType === TravelRuleType.Business,
					})}
					onClick={() =>
						dispatch(PreorderActions.setTravelRuleType(TravelRuleType.Business))
					}
				>
					{formatMessage(messages.business)}
				</span>
			</div>
			<div className={styles.form}>
				{travelRuleType === TravelRuleType.Personal ? (
					<>
						<div className={styles.personalNames}>
							<Input
								data-cy={TestIds.PreorderTravelRuleFirstNameInput}
								label={formatMessage(messages.firstName)}
								placeholder={formatMessage(messages.firstAndMiddleNames)}
								value={firstName || ''}
								onChange={(value) => dispatch(PreorderActions.setFirstName(value))}
								errorMessage={validationResult.firstName}
								moreSpaceForError={!!validationResult.firstName}
							/>
							<Input
								data-cy={TestIds.PreorderTravelRuleLastNameInput}
								label={formatMessage(messages.lastName)}
								placeholder={formatMessage(messages.lastNames)}
								value={lastName || ''}
								onChange={(value) => dispatch(PreorderActions.setLastName(value))}
								errorMessage={validationResult.lastName}
								moreSpaceForError={!!validationResult.lastName}
							/>
						</div>
						<DatePicker
							className={styles.dateOfBirth}
							label={messages.dateOfBirth}
							placeholderText="YYYY-MM-DD"
							onChange={(date: Date | null) =>
								dispatch(PreorderActions.setDateOfBirth(date))
							}
							selected={dateOfBirth}
							data-cy={TestIds.PreorderTravelRuleDateOfBirthInput}
						/>
						<SelectInput
							data-cy={TestIds.PreorderTravelRuleCountryOfResidenceInput}
							label={formatMessage(messages.countryOfResidence)}
							options={countryOptions}
							value={countryOfResidence}
							onChange={(value: string) =>
								dispatch(PreorderActions.setCountryOfResidence(value))
							}
						/>
					</>
				) : (
					<>
						<Input
							data-cy={TestIds.PreorderTravelRuleCompanyNameInput}
							label={formatMessage(messages.companyName)}
							placeholder={formatMessage(messages.companyNamePlaceholder)}
							value={companyName || ''}
							onChange={(value) => dispatch(PreorderActions.setCompanyName(value))}
							errorMessage={validationResult.companyName}
						/>
						<Input
							data-cy={TestIds.PreorderTravelRuleCompanyCodeInput}
							label={formatMessage(messages.companyCode)}
							placeholder={formatMessage(messages.companyCodePlaceholder)}
							value={companyCode || ''}
							onChange={(value) => dispatch(PreorderActions.setCompanyCode(value))}
							errorMessage={validationResult.companyCode}
						/>
						<SelectInput
							data-cy={TestIds.PreorderTravelRuleCountryOfRegistrationInput}
							label={formatMessage(messages.countryOfRegistration)}
							options={countryOptions}
							value={countryOfRegistration}
							onChange={(value: string) =>
								dispatch(PreorderActions.setCountryOfRegistration(value))
							}
						/>
					</>
				)}
				<Input
					data-cy={TestIds.PreorderTravelRuleEmailInput}
					label={formatMessage(messages.email)}
					placeholder={formatMessage(messages.enterEmailAddress)}
					disclaimer={messages.emailDisclaimer}
					value={email || ''}
					onChange={(value) => dispatch(PreorderActions.setEmail(value))}
					errorMessage={debouncedEmailError}
				/>
			</div>
		</div>
	);
};

const PrivacyLinkHandler = (text: ReactNode) => (
	<a
		href="https://spectrocoin.com/en/terms/privacy-policy-eu.html"
		target="_blank"
		rel="noreferrer"
	>
		{text}
	</a>
);

const ShopperDisclaimerLinkHandler = (text: ReactNode) => (
	<a
		href="https://help.spectrocoin.com/en-US/kb/article/127/shopper-disclaimer"
		target="_blank"
		rel="noreferrer"
	>
		{text}
	</a>
);

const PreorderTravelRule: FC = () => {
	const { formatMessage } = useIntl();
	const dispatch = useAppDispatch();
	const {
		travelRuleType,
		firstName,
		lastName,
		dateOfBirth,
		companyName,
		companyCode,
		countryOfResidence,
		countryOfRegistration,
		email,
		// saveInformationChecked,
		confirmChecked,
	} = useSelector(getTravelRuleForm);
	const validator = useFormValidator();
	const [isValid, validationResult] = useValidation(
		{
			firstName,
			lastName,
			companyName,
			companyCode,
			email,
		},
		validator
	);

	const isFormFilled = useMemo(() => {
		const isBaseFilled = isValid && confirmChecked && !!email;
		if (travelRuleType === TravelRuleType.Personal) {
			return (
				isBaseFilled && !!firstName && !!lastName && !!dateOfBirth && !!countryOfResidence
			);
		}
		return isBaseFilled && !!companyName && !!companyCode && !!countryOfRegistration;
	}, [
		isValid,
		confirmChecked,
		travelRuleType,
		firstName,
		lastName,
		dateOfBirth,
		companyName,
		companyCode,
		countryOfResidence,
		countryOfRegistration,
		email,
	]);

	const goToCurrencySelect = useCallback(() => {
		dispatch(PreorderActions.setStage(PreorderStage.CurrencySelect));
	}, [dispatch]);

	return (
		<div className={styles.body} data-cy={TestIds.Preorder}>
			<div className={styles.content}>
				<span className={styles.enterYourInformation}>
					{formatMessage(messages.enterYourInformation)}
				</span>
				<Form validationResult={validationResult} />
				{/* <Checkbox
					data-cy={TestIds.PreorderTravelRuleSaveInformationCheckbox}
					checked={saveInformationChecked}
					onChange={() => {
						dispatch(
							PreorderActions.setSaveInformationChecked(!saveInformationChecked)
						);
					}}
				>
					<FormattedMessage {...messages.saveTravelRuleInformation} />
				</Checkbox> */}
				<Checkbox
					data-cy={TestIds.PreorderTravelRuleConfirmCheckbox}
					checked={confirmChecked}
					onChange={() => {
						dispatch(PreorderActions.setConfirmChecked(!confirmChecked));
					}}
				>
					<FormattedMessage
						{...messages.travelRuleConfirm}
						values={{
							privacyLink: PrivacyLinkHandler,
							shopperDisclaimerLink: ShopperDisclaimerLinkHandler,
						}}
					/>
				</Checkbox>
				<MessageBlock
					withIcon
					data-cy={TestIds.PreorderTravelRulePaymentInfo}
					message={formatMessage(messages.travelRuleInfo)}
					type={MessageType.Security}
					style={BlockStyle.Fill}
				/>
			</div>
			<Button
				isFullWidth
				data-cy={TestIds.PreorderTravelRuleConfirm}
				className={styles.button}
				onClick={goToCurrencySelect}
				text={formatMessage(messages.button)}
				type={ButtonType.BUTTON}
				isDisabled={!isFormFilled}
			/>
		</div>
	);
};

export default PreorderTravelRule;
